/**
 * API Error Utilities
 * Provides utilities for handling API errors consistently across the application
 */

export class ApiError extends Error {
  constructor(
    public statusCode: number,
    public code: string,
    message: string,
    public details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /**
   * Check if error is a rate limit error (429)
   */
  static isRateLimitError(error: unknown): error is ApiError {
    return error instanceof ApiError && error.statusCode === 429;
  }

  /**
   * Check if error is a not found error (404)
   */
  static isNotFoundError(error: unknown): error is ApiError {
    return error instanceof ApiError && error.statusCode === 404;
  }

  /**
   * Check if error is an authentication error (401)
   */
  static isAuthError(error: unknown): error is ApiError {
    return error instanceof ApiError && error.statusCode === 401;
  }

  /**
   * Check if error is a forbidden error (403)
   */
  static isForbiddenError(error: unknown): error is ApiError {
    return error instanceof ApiError && error.statusCode === 403;
  }

  /**
   * Check if error is a validation error (400)
   */
  static isValidationError(error: unknown): error is ApiError {
    return error instanceof ApiError && error.statusCode === 400;
  }

  /**
   * Check if error is a server error (5xx)
   */
  static isServerError(error: unknown): error is ApiError {
    return error instanceof ApiError && error.statusCode >= 500 && error.statusCode < 600;
  }
}

/**
 * Get a user-friendly error message from any error
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    // Friendly messages for common errors
    if (error.statusCode === 429) {
      return 'Too many requests. Please wait a moment before trying again.';
    }
    if (error.statusCode === 404) {
      return 'Container not found. Please check your information.';
    }
    if (error.statusCode === 401) {
      return 'Your session has expired. Please start over.';
    }
    if (error.statusCode === 403) {
      return 'You do not have permission to perform this action.';
    }
    if (error.statusCode === 400) {
      return error.message || 'Invalid request. Please check your information.';
    }
    if (error.statusCode >= 500) {
      return 'Server error. Please try again later or contact support.';
    }
    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  return 'An unexpected error occurred. Please try again.';
}

/**
 * Get retry delay for rate limit errors
 * Extracts the Retry-After header value if available
 */
export function getRetryDelay(error: unknown): number | null {
  if (ApiError.isRateLimitError(error) && error.details) {
    const details = error.details as { retryAfter?: number };
    return details.retryAfter || 60; // Default to 60 seconds
  }
  return null;
}

/**
 * Create an ApiError from a fetch response
 */
export async function createApiErrorFromResponse(response: Response): Promise<ApiError> {
  let errorData: { code?: string; message?: string; details?: unknown } = {};

  try {
    errorData = await response.json();
  } catch {
    // If response body is not JSON, use status text
    return new ApiError(
      response.status,
      'NETWORK_ERROR',
      response.statusText || 'Network error occurred'
    );
  }

  return new ApiError(
    response.status,
    errorData.code || 'UNKNOWN_ERROR',
    errorData.message || `HTTP ${response.status}: ${response.statusText}`,
    errorData.details
  );
}

/**
 * Wrap an error in ApiError if it's not already one
 */
export function normalizeError(error: unknown): ApiError {
  if (error instanceof ApiError) {
    return error;
  }

  if (error instanceof Error) {
    return new ApiError(500, 'INTERNAL_ERROR', error.message);
  }

  if (typeof error === 'string') {
    return new ApiError(500, 'INTERNAL_ERROR', error);
  }

  return new ApiError(500, 'UNKNOWN_ERROR', 'An unexpected error occurred');
}
